// src/components/Shared/feedback.jsx
// Imperative replacement for native window.confirm / window.prompt / window.alert.
//
// Usage:
//   import { confirm, prompt, toast, FeedbackHost } from '.../Shared/feedback';
//
//   if (!(await confirm({ title: 'Delete this user?', tone: 'danger', confirmLabel: 'Delete' }))) return;
//   const qty = await prompt({ title: 'Enter new quantity', defaultValue: '50', inputType: 'number' });
//   toast.success('Invite link copied');
//
// One `<FeedbackHost />` must be mounted once near the root of the app (in
// App.js). The imperative functions register callbacks against an internal
// store; the host subscribes and renders the active dialog / toast list.

import React, { useEffect, useRef, useState, useCallback } from 'react';

// ---- Internal store -------------------------------------------------------
let nextId = 1;
const listeners = new Set();
const state = {
	dialog: null, // { id, kind: 'confirm'|'prompt', config, resolve }
	toasts: [],   // [{ id, tone, message, ttl }]
};

const emit = () => {
	listeners.forEach((l) => l({ ...state, toasts: [...state.toasts] }));
};

const subscribe = (fn) => {
	listeners.add(fn);
	return () => listeners.delete(fn);
};

// ---- Imperative API -------------------------------------------------------

/**
 * Show a confirm dialog. Resolves with `true` if confirmed, `false` if cancelled.
 * @param {object} config
 * @param {string} config.title
 * @param {string} [config.message]
 * @param {string} [config.confirmLabel='Confirm']
 * @param {string} [config.cancelLabel='Cancel']
 * @param {'default'|'danger'|'primary'} [config.tone='default']
 */
export const confirm = (config = {}) =>
	new Promise((resolve) => {
		state.dialog = {
			id: nextId++,
			kind: 'confirm',
			config: {
				title: config.title || 'Are you sure?',
				message: config.message,
				confirmLabel: config.confirmLabel || 'Confirm',
				cancelLabel: config.cancelLabel || 'Cancel',
				tone: config.tone || 'default',
			},
			resolve,
		};
		emit();
	});

/**
 * Show a prompt dialog. Resolves with the input string, or `null` if cancelled.
 * @param {object} config
 * @param {string} config.title
 * @param {string} [config.message]
 * @param {string} [config.defaultValue='']
 * @param {string} [config.placeholder]
 * @param {'text'|'number'} [config.inputType='text']
 * @param {string} [config.confirmLabel='Save']
 * @param {string} [config.cancelLabel='Cancel']
 */
export const prompt = (config = {}) =>
	new Promise((resolve) => {
		state.dialog = {
			id: nextId++,
			kind: 'prompt',
			config: {
				title: config.title || 'Enter a value',
				message: config.message,
				defaultValue: config.defaultValue ?? '',
				placeholder: config.placeholder,
				inputType: config.inputType || 'text',
				confirmLabel: config.confirmLabel || 'Save',
				cancelLabel: config.cancelLabel || 'Cancel',
			},
			resolve,
		};
		emit();
	});

const dismissToast = (id) => {
	state.toasts = state.toasts.filter((t) => t.id !== id);
	emit();
};

const pushToast = (tone, message, options = {}) => {
	const id = nextId++;
	// Support legacy signature: pushToast('success', 'msg', 5000)
	const opts = typeof options === 'number' ? { ttl: options } : (options || {});
	const sticky = !!opts.sticky;
	const ttl = opts.ttl ?? (sticky ? 0 : (tone === 'error' ? 6000 : 4000));
	state.toasts.push({ id, tone, message, ttl, sticky });
	emit();
	if (!sticky && ttl > 0) {
		setTimeout(() => dismissToast(id), ttl);
	}
	return id;
};

export const toast = {
	success: (message, opts) => pushToast('success', message, opts),
	error: (message, opts) => pushToast('error', message, opts),
	info: (message, opts) => pushToast('info', message, opts),
	warn: (message, opts) => pushToast('warning', message, opts),
	// Sticky variant — requires user dismissal. Use for critical messages
	// the user must actually see before continuing.
	important: (message, tone = 'info') => pushToast(tone, message, { sticky: true }),
	dismiss: dismissToast,
};

// ---- Host component -------------------------------------------------------

const DialogShell = ({ dialog, onClose }) => {
	const { kind, config, resolve } = dialog;
	const [value, setValue] = useState(config.defaultValue || '');
	const inputRef = useRef(null);

	useEffect(() => {
		// Autofocus the input on prompt, the confirm button on confirm
		const t = setTimeout(() => {
			if (kind === 'prompt' && inputRef.current) {
				inputRef.current.focus();
				inputRef.current.select?.();
			}
		}, 30);
		return () => clearTimeout(t);
	}, [kind]);

	useEffect(() => {
		const onKey = (e) => {
			if (e.key === 'Escape') {
				e.preventDefault();
				handleCancel();
			} else if (e.key === 'Enter' && kind === 'prompt' && document.activeElement === inputRef.current) {
				e.preventDefault();
				handleConfirm();
			}
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value]);

	const handleConfirm = useCallback(() => {
		if (kind === 'confirm') {
			resolve(true);
		} else {
			resolve(value);
		}
		onClose();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [kind, resolve, value, onClose]);

	const handleCancel = useCallback(() => {
		resolve(kind === 'confirm' ? false : null);
		onClose();
	}, [kind, resolve, onClose]);

	const confirmClass =
		config.tone === 'danger'
			? 'ev-dialog-confirm is-danger'
			: config.tone === 'primary'
			? 'ev-dialog-confirm is-primary'
			: 'ev-dialog-confirm';

	return (
		<div className="ev-dialog-backdrop" role="presentation" onClick={handleCancel}>
			<div
				className="ev-dialog"
				role="dialog"
				aria-modal="true"
				aria-labelledby="ev-dialog-title"
				onClick={(e) => e.stopPropagation()}
			>
				<h2 id="ev-dialog-title" className="ev-dialog-title">{config.title}</h2>
				{config.message && <p className="ev-dialog-message">{config.message}</p>}

				{kind === 'prompt' && (
					<input
						ref={inputRef}
						type={config.inputType}
						className="ev-dialog-input"
						value={value}
						onChange={(e) => setValue(e.target.value)}
						placeholder={config.placeholder}
					/>
				)}

				<div className="ev-dialog-actions">
					<button type="button" className="ev-dialog-cancel" onClick={handleCancel}>
						{config.cancelLabel}
					</button>
					<button type="button" className={confirmClass} onClick={handleConfirm}>
						{config.confirmLabel}
					</button>
				</div>
			</div>
		</div>
	);
};

const TOAST_GLYPHS = {
	success: (
		<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
			<polyline points="5 12 10 17 19 7" />
		</svg>
	),
	error: (
		<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
			<line x1="6" y1="6" x2="18" y2="18" />
			<line x1="18" y1="6" x2="6" y2="18" />
		</svg>
	),
	warning: (
		<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
			<line x1="12" y1="7" x2="12" y2="13" />
			<circle cx="12" cy="17" r="0.6" fill="currentColor" stroke="none" />
		</svg>
	),
	info: (
		<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
			<line x1="12" y1="11" x2="12" y2="17" />
			<circle cx="12" cy="7" r="0.6" fill="currentColor" stroke="none" />
		</svg>
	),
};

const ToastList = ({ toasts }) => (
	<div className="ev-toast-stack" role="status" aria-live="polite">
		{toasts.map((t) => (
			<div key={t.id} className={`ev-toast is-${t.tone}${t.sticky ? ' is-sticky' : ''}`} role={t.tone === 'error' || t.sticky ? 'alert' : 'status'}>
				<span className="ev-toast-icon" aria-hidden="true">{TOAST_GLYPHS[t.tone] || TOAST_GLYPHS.info}</span>
				<span className="ev-toast-msg">{t.message}</span>
				<button
					type="button"
					className="ev-toast-close"
					aria-label="Dismiss notification"
					onClick={() => dismissToast(t.id)}
				>
					×
				</button>
			</div>
		))}
	</div>
);

export const FeedbackHost = () => {
	const [snapshot, setSnapshot] = useState({ dialog: null, toasts: [] });
	useEffect(() => subscribe(setSnapshot), []);

	const closeDialog = useCallback(() => {
		state.dialog = null;
		emit();
	}, []);

	return (
		<>
			{snapshot.dialog && <DialogShell dialog={snapshot.dialog} onClose={closeDialog} />}
			{snapshot.toasts.length > 0 && <ToastList toasts={snapshot.toasts} />}
		</>
	);
};

export default FeedbackHost;
